import { useQuery } from '@tanstack/react-query';
import { apiService } from '../../services/api';
import type {
  FlightShareListResponse,
  ShareCounterparties,
  ShareDirection,
  ShareableDronesResponse,
} from '../../types/api';

// STD-015: server state for the flight-sharing domain (#1182 Stage 3).
// apiService is imported here (a *.queries.ts file) per STD-016.

export interface FlightShareListFilters {
  flightId?: string;
  droneSerial?: string;
  includeRevoked?: boolean;
  /** Which side of the share to list. Omitted = both. */
  direction?: ShareDirection;
  /** The other organization on the share — grantee outbound, grantor inbound. */
  counterpartyOrganizationId?: string;
  limit?: number;
  offset?: number;
}

export const flightSharesKeys = {
  all: ['flight-shares'] as const,
  list: (filters: FlightShareListFilters) => [...flightSharesKeys.all, 'list', filters] as const,
  counterparties: () => [...flightSharesKeys.all, 'counterparties'] as const,
  shareableDrones: () => [...flightSharesKeys.all, 'shareable-drones'] as const,
};

/**
 * Shares the caller's org granted or received.
 *
 * `enabled` is a parameter rather than derived from the filters: an unfiltered
 * list (the "Shared" view) is a legitimate query, so "no filters" must not mean
 * "don't run". The caller decides — the dialogs pass `open`, the view passes
 * `true`.
 *
 * Returns the whole envelope rather than just `shares`, because `total` is not
 * derivable from a page: `shares.length` is at most `limit`, so a view that
 * unwrapped the envelope could only ever draw a pager over the page it is
 * already showing.
 */
export function useFlightSharesQuery(filters: FlightShareListFilters, enabled = true) {
  return useQuery<FlightShareListResponse, Error>({
    queryKey: flightSharesKeys.list(filters),
    enabled,
    // Both consumers render the failure inline and keep it on screen. Leaving
    // the global toast on as well would report the same failure twice.
    meta: { skipGlobalErrorToast: true },
    queryFn: async () => apiService.listFlightShares(filters),
  });
}

/**
 * The organizations this caller may legally share with, plus its own org id.
 *
 * `staleTime` is generous because the answer is the hub-and-spoke topology,
 * which changes only when an organization is created. Refetching it on every
 * dialog open would be a request per right-click.
 *
 * The global error toast is suppressed: this query is admin-gated
 * (`require_org_admin`), so a non-admin who somehow reaches a surface that
 * mounts it should see the surface degrade, not a toast blaming them.
 */
export function useShareCounterpartiesQuery(enabled = true) {
  return useQuery<ShareCounterparties, Error>({
    queryKey: flightSharesKeys.counterparties(),
    enabled,
    staleTime: 5 * 60 * 1000,
    meta: { skipGlobalErrorToast: true },
    queryFn: async () => apiService.getShareCounterparties(),
  });
}

/**
 * The drones this caller's org can share flights of — the bulk dialog's picker
 * (#1498). Same posture as the counterparties query: admin-gated on the server,
 * enabled only while the dialog is open, and the global toast suppressed because
 * the dialog renders the failure inline next to the control it empties.
 *
 * A shorter `staleTime` than the counterparties: this answer changes with every
 * upload, and a dialog reopened after a MissionPlanner upload should offer the
 * new drone without a page reload.
 */
export function useShareableDronesQuery(enabled = true) {
  return useQuery<ShareableDronesResponse, Error>({
    queryKey: flightSharesKeys.shareableDrones(),
    enabled,
    staleTime: 60 * 1000,
    meta: { skipGlobalErrorToast: true },
    queryFn: async () => apiService.getShareableDrones(),
  });
}
